import React, { useState } from 'react';
import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  MicrophoneIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useAppStore, useTheme, useNotifications, useUnreadNotificationsCount } from '@/store';
import { useAuth } from '@/contexts/AuthContext';
import VoiceInterface from '@/components/VoiceInterface';

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  const navigate = useNavigate();
  const setSidebarOpen = useAppStore((state) => state.setSidebarOpen);
  const toggleSidebar = useAppStore((state) => state.toggleSidebar);
  const theme = useTheme();
  const setTheme = useAppStore((state) => state.setTheme);
  const notifications = useNotifications();
  const unreadCount = useUnreadNotificationsCount();
  const { user, logout } = useAuth();

  const themeOptions = [
    { value: 'light', label: 'Светлая', icon: SunIcon },
    { value: 'dark', label: 'Темная', icon: MoonIcon },
    { value: 'system', label: 'Системная', icon: ComputerDesktopIcon },
  ] as const;

  const currentThemeOption = themeOptions.find(option => option.value === theme);

  return (
    <>
      <header className="w-full bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Левая часть */}
            <div className="flex items-center">
              {/* Кнопка меню для мобильных */}
              <button
                type="button"
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                onClick={onMenuClick || toggleSidebar}
              >
                <span className="sr-only">Открыть меню</span>
                <Bars3Icon className="h-6 w-6" />
              </button>

              {/* Логотип и название */}
              <div className="flex items-center ml-4 lg:ml-0">
                <div className="flex-shrink-0 flex items-center">
                  <span className="text-2xl">🪱</span>
                  <h1 className="ml-2 text-xl font-bold text-gray-900 dark:text-gray-100">
                    WormTracker
                  </h1>
                </div>
              </div>
            </div>

            {/* Правая часть */}
            <div className="flex items-center space-x-4">
              {/* Голосовой интерфейс */}
              <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                title="Голосовые команды"
              >
                <MicrophoneIcon className="h-6 w-6" />
              </button>

              {/* Переключатель темы */}
              <Menu as="div" className="relative">
                <Menu.Button className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors">
                  {currentThemeOption && (
                    <currentThemeOption.icon className="h-6 w-6" />
                  )}
                </Menu.Button>
                
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {themeOptions.map((option) => (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            onClick={() => setTheme(option.value)}
                            className={clsx(
                              active ? 'bg-gray-100 dark:bg-gray-700' : '',
                              theme === option.value ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
                              'flex w-full items-center px-4 py-2 text-sm'
                            )}
                          >
                            <option.icon className="mr-3 h-5 w-5" />
                            {option.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* Уведомления */}
              <Menu as="div" className="relative">
                <Menu.Button className="relative p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors">
                  <BellIcon className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 h-3 w-3 bg-red-500 rounded-full">
                      <span className="sr-only">{unreadCount} непрочитанных уведомлений</span>
                    </span>
                  )}
                </Menu.Button>
                
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-80 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Уведомления
                          {unreadCount > 0 && (
                            <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">
                              ({unreadCount})
                            </span>
                          )}
                        </h3>
                      </div>
                      
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          Нет уведомлений
                        </div>
                      ) : (
                        <div className="max-h-64 overflow-y-auto">
                          {notifications.slice(0, 5).map((notification) => (
                            <Menu.Item key={notification.id}>
                              {({ active }) => (
                                <div
                                  className={clsx(
                                    active ? 'bg-gray-50 dark:bg-gray-700' : '',
                                    !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : '',
                                    'px-4 py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0'
                                  )}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {notification.title}
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        {notification.message}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                        {new Date(notification.timestamp).toLocaleString('ru-RU')}
                                      </p>
                                    </div>
                                    {!notification.read && (
                                      <div className="ml-2 h-2 w-2 bg-blue-500 rounded-full"></div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      )}
                      
                      {notifications.length > 5 && (
                        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                          <button className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                            Показать все
                          </button>
                        </div>
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* Профиль пользователя */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-400" />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {/* Информация о пользователе */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium text-lg">
                          {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user?.full_name || user?.username}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            @{user?.username}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 px-2 py-1 bg-primary-50 dark:bg-primary-900/20 rounded-md">
                        <p className="text-xs font-medium text-primary-700 dark:text-primary-300">
                          {user?.role === 'admin' ? '👑 Администратор' : '👤 Работник'}
                        </p>
                      </div>
                    </div>

                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => navigate('/settings')}
                            className={clsx(
                              active ? 'bg-gray-100 dark:bg-gray-700' : '',
                              'flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                            )}
                          >
                            <Cog6ToothIcon className="mr-3 h-5 w-5 text-gray-400" />
                            Настройки
                          </button>
                        )}
                      </Menu.Item>

                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => setAboutOpen(true)}
                            className={clsx(
                              active ? 'bg-gray-100 dark:bg-gray-700' : '',
                              'flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                            )}
                          >
                            <InformationCircleIcon className="mr-3 h-5 w-5 text-gray-400" />
                            О программе
                          </button>
                        )}
                      </Menu.Item>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={logout}
                            className={clsx(
                              active ? 'bg-red-50 dark:bg-red-900/20' : '',
                              'flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 font-medium'
                            )}
                          >
                            <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                            Выйти из системы
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      </header>

      {/* Голосовой интерфейс */}
      <VoiceInterface
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
      />

      {/* Модальное окно "О программе" */}
      {aboutOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="text-4xl">🪱</div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  WormTracker
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Версия 1.0.0
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Система управления вермифермой для мониторинга и контроля процессов производства биогумуса.
              </p>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Возможности:
                </h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  <li>• Управление партиями червей</li>
                  <li>• Мониторинг датчиков (температура, влажность, CO₂)</li>
                  <li>• Управление реле и автоматизацией</li>
                  <li>• Учёт продаж и складских запасов</li>
                  <li>• Голосовое управление</li>
                  <li>• Управление пользователями и ролями</li>
                </ul>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  © 2025 WormTracker. Все права защищены.
                </p>
              </div>
            </div>

            <button
              onClick={() => setAboutOpen(false)}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
